<div class="min-h-screen bg-base-200/40 text-base-content py-8 pb-16">
  <div class="container mx-auto px-4 max-w-6xl">
    <!-- Breadcrumb -->
    <nav class="daisy-breadcrumbs text-xs sm:text-sm mb-4" aria-label="Breadcrumb">
      <ul>
        <li><a routerLink="/" class="text-base-content/70 hover:text-primary">Home</a></li>
        <li><a routerLink="/courses" class="text-base-content/70 hover:text-primary">Courses</a></li>
        @if (selectedCourse) {
          <li><a [routerLink]="['/courses', selectedCourse._id]" class="text-base-content/70 hover:text-primary truncate max-w-xs">{{ selectedCourse.title }}</a></li>
        }
        <li class="font-medium text-base-content">Enrollment</li>
      </ul>
    </nav>

    <!-- Header Section -->
    <div class="mb-8">
      <h1 class="text-2xl sm:text-3xl font-extrabold text-base-content tracking-tight m-0">
        Course Enrollment
      </h1>
      <p class="text-sm sm:text-base text-base-content/70 mt-1">
        Select a payment option that best fits your budget to unlock immediate course access.
      </p>
    </div>

    @if (selectedCourse) {
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <!-- Left Column: Course Preview Card (Sticky) -->
        <div class="lg:col-span-5">
          <div class="sticky top-20 daisy-card daisy-card-bordered bg-base-100 border-base-300 shadow-md overflow-hidden">
            <figure class="relative aspect-video w-full m-0 bg-base-200 overflow-hidden">
              @if (selectedCourse.settings.bannerImageResource.path) {
                <img
                  [src]="selectedCourse.settings.bannerImageResource.path"
                  [alt]="selectedCourse.title"
                  class="w-full h-full object-cover"
                />
              } @else {
                <div class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-base-content/40 bg-base-200">
                  <i class="fa-solid fa-graduation-cap text-4xl"></i>
                </div>
              }
              <div class="absolute top-3 left-3 flex flex-wrap gap-1">
                @if (selectedCourse.targetAudience) {
                  <span class="daisy-badge daisy-badge-primary font-bold text-[0.65rem] uppercase tracking-wider shadow-sm">
                    {{ selectedCourse.targetAudience }}
                  </span>
                }
                @if (selectedCourse.subject) {
                  <span class="daisy-badge daisy-badge-neutral text-white font-bold text-[0.65rem] uppercase tracking-wider shadow-sm">
                    {{ selectedCourse.subject }}
                  </span>
                }
              </div>
            </figure>

            <div class="daisy-card-body p-5 space-y-4">
              <h2 class="font-bold text-lg text-base-content m-0 leading-snug">
                {{ selectedCourse.title }}
              </h2>
              @if (selectedCourse.details.descriptionSummary) {
                <p class="text-xs text-base-content/70 line-clamp-3 m-0 leading-relaxed">
                  {{ selectedCourse.details.descriptionSummary }}
                </p>
              }

              <!-- Course Inclusions -->
              <div class="pt-3 border-t border-base-200 space-y-2 text-xs text-base-content/70">
                <div class="flex items-center gap-2.5">
                  <i class="fa-solid fa-circle-check text-success text-sm"></i>
                  <span>Full lifetime access to course lessons</span>
                </div>
                <div class="flex items-center gap-2.5">
                  <i class="fa-solid fa-file-arrow-down text-primary text-sm"></i>
                  <span>Downloadable lecture materials</span>
                </div>
                <div class="flex items-center gap-2.5">
                  <i class="fa-solid fa-mobile-screen-button text-primary text-sm"></i>
                  <span>Accessible on mobile, tablet & desktop</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Plan Selection & Checkout -->
        <div class="lg:col-span-7 space-y-6">
          @if (isEnrolled) {
            <!-- Already Enrolled Notice -->
            <div class="daisy-card daisy-card-bordered bg-base-100 border-base-300 shadow-sm p-6">
              <div class="daisy-alert daisy-alert-success text-sm">
                <i class="fa-solid fa-circle-check text-lg"></i>
                <div>
                  <h4 class="font-bold">You are already enrolled!</h4>
                  <p class="text-xs mt-0.5">You have active access to this course. You can continue learning right away.</p>
                </div>
              </div>
              <button
                type="button"
                class="daisy-btn daisy-btn-primary mt-4 w-full gap-2 shadow-md"
                (click)="goToLearning()"
              >
                <i class="fa-solid fa-circle-play"></i>
                <span>Continue Learning</span>
              </button>
            </div>
          } @else {
            <!-- Payment Plan Selection -->
            <div class="daisy-card daisy-card-bordered bg-base-100 border-base-300 shadow-sm p-6 space-y-5">
              <div>
                <h3 class="text-lg font-bold text-base-content m-0">Choose Your Payment Plan</h3>
                <p class="text-xs text-base-content/60 mt-1">Select full payment or split tuition into installments.</p>
              </div>

              <div class="space-y-3">
                <!-- Option 1: One-Time Full Payment -->
                <div
                  class="daisy-card daisy-card-bordered p-4 cursor-pointer transition-all border-2"
                  [class.border-primary]="selectedPlan === 'FULL'"
                  [class.bg-primary/5]="selectedPlan === 'FULL'"
                  [class.border-base-300]="selectedPlan !== 'FULL'"
                  [class.hover:border-primary/40]="selectedPlan !== 'FULL'"
                  (click)="onSelectPlan('FULL')"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="flex items-start gap-3">
                      <input
                        type="radio"
                        name="paymentPlan"
                        class="daisy-radio daisy-radio-primary mt-1"
                        [checked]="selectedPlan === 'FULL'"
                        (change)="onSelectPlan('FULL')"
                      />
                      <div>
                        <div class="flex items-center gap-2">
                          <h4 class="font-bold text-sm text-base-content m-0">Full Payment (One-Time)</h4>
                          <span class="daisy-badge daisy-badge-primary daisy-badge-sm font-semibold">Best Value</span>
                        </div>
                        <p class="text-xs text-base-content/60 mt-0.5 m-0">Pay once upfront for complete access with no recurring obligations.</p>
                      </div>
                    </div>
                    <div class="text-right shrink-0">
                      <span class="text-lg font-extrabold text-primary">{{ priceFormatted }}</span>
                      <p class="text-[0.65rem] text-base-content/50 uppercase font-semibold m-0">Single charge</p>
                    </div>
                  </div>
                </div>

                <!-- Option 2: Installment Plan (if configured) -->
                @if (hasInstallmentOptions) {
                  <div
                    class="daisy-card daisy-card-bordered p-4 cursor-pointer transition-all border-2"
                    [class.border-primary]="selectedPlan === 'INSTALLMENT'"
                    [class.bg-primary/5]="selectedPlan === 'INSTALLMENT'"
                    [class.border-base-300]="selectedPlan !== 'INSTALLMENT'"
                    [class.hover:border-primary/40]="selectedPlan !== 'INSTALLMENT'"
                    (click)="onSelectPlan('INSTALLMENT')"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <div class="flex items-start gap-3">
                        <input
                          type="radio"
                          name="paymentPlan"
                          class="daisy-radio daisy-radio-primary mt-1"
                          [checked]="selectedPlan === 'INSTALLMENT'"
                          (change)="onSelectPlan('INSTALLMENT')"
                        />
                        <div>
                          <div class="flex items-center gap-2">
                            <h4 class="font-bold text-sm text-base-content m-0">Installment Plan</h4>
                            <span class="daisy-badge daisy-badge-secondary daisy-badge-sm font-semibold">Flexible</span>
                          </div>
                          <p class="text-xs text-base-content/60 mt-0.5 m-0">
                            Pay in {{ installmentCount }} scheduled payments of LKR {{ installmentBaseAmount.toLocaleString('en-LK') }} every {{ installmentIntervalDays }} days.
                          </p>
                        </div>
                      </div>
                      <div class="text-right shrink-0">
                        <span class="text-lg font-extrabold text-primary">
                          LKR {{ firstInstallmentAmount.toLocaleString('en-LK') }}
                        </span>
                        <p class="text-[0.65rem] text-base-content/50 uppercase font-semibold m-0">Due today</p>
                      </div>
                    </div>

                    <!-- Installment Schedule Breakdown -->
                    @if (selectedPlan === 'INSTALLMENT') {
                      <div class="mt-4 pt-4 border-t border-base-200">
                        <p class="text-xs font-semibold text-base-content/80 mb-2.5 flex items-center gap-1.5">
                          <i class="fa-solid fa-calendar-check text-primary"></i>
                          Payment Schedule:
                        </p>
                        <div class="space-y-2">
                          @for (inst of installmentSchedule; track inst.number) {
                            <div class="flex items-center justify-between text-xs py-1.5 px-2.5 rounded-lg bg-base-200/60">
                              <div class="flex items-center gap-2">
                                <span class="w-5 h-5 rounded-full bg-primary/20 text-primary font-bold flex items-center justify-center text-[10px]">
                                  {{ inst.number }}
                                </span>
                                <span class="font-medium text-base-content">
                                  {{ inst.number === 1 ? 'Installment 1 (Due Today)' : 'Installment ' + inst.number }}
                                </span>
                              </div>
                              <div class="flex items-center gap-3">
                                <span class="text-base-content/60">
                                  {{ inst.number === 1 ? 'Immediate' : (inst.dueDate | date: 'mediumDate') }}
                                </span>
                                <span class="font-bold text-base-content">
                                  LKR {{ inst.amount.toLocaleString('en-LK') }}
                                </span>
                              </div>
                            </div>
                          }
                        </div>
                        <p class="text-[0.7rem] text-base-content/50 mt-2 italic m-0">
                          * A 5-day grace period is provided after each due date before course access pauses.
                        </p>
                      </div>
                    }
                  </div>
                }
              </div>

              <!-- Order Summary & Checkout Action -->
              <div class="pt-4 border-t border-base-200 space-y-4">
                <div class="space-y-2 text-sm">
                  <div class="flex justify-between text-base-content/70">
                    <span>Course Tuition</span>
                    <span class="font-medium">{{ priceFormatted }}</span>
                  </div>
                  <div class="flex justify-between text-base-content/70">
                    <span>Selected Plan</span>
                    <span class="font-medium">
                      {{ selectedPlan === 'FULL' ? 'Full Payment' : (installmentCount + ' Installments') }}
                    </span>
                  </div>
                  <div class="flex justify-between items-baseline pt-2 border-t border-base-200">
                    <span class="font-bold text-base text-base-content">Amount Due Today</span>
                    <span class="text-2xl font-extrabold text-primary">
                      {{ selectedPlan === 'FULL' ? priceFormatted : 'LKR ' + firstInstallmentAmount.toLocaleString('en-LK') }}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  class="daisy-btn daisy-btn-primary daisy-btn-block daisy-btn-lg gap-2 shadow-lg"
                  [disabled]="isSubmitting"
                  (click)="proceedToPayment()"
                >
                  <i class="fa-solid fa-lock text-sm"></i>
                  @if (isSubmitting) {
                    <span>Processing...</span>
                  } @else {
                    <span>Proceed to Payment</span>
                  }
                </button>

                <div class="flex items-center justify-center gap-4 text-xs text-base-content/50 pt-1">
                  <span class="flex items-center gap-1">
                    <i class="fa-solid fa-shield-halved text-success"></i>
                    Secure Checkout
                  </span>
                  <span>•</span>
                  <span class="flex items-center gap-1">
                    <i class="fa-solid fa-bolt text-primary"></i>
                    Instant Access
                  </span>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    } @else {
      <!-- Skeleton Loading State -->
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start animate-pulse">
        <div class="lg:col-span-5 h-80 bg-base-300 rounded-2xl"></div>
        <div class="lg:col-span-7 h-96 bg-base-300 rounded-2xl"></div>
      </div>
    }
  </div>
</div>
